import { enableChartsExporting } from '@ecosystem/export-utils';
import { InsightRendererProps } from '@ecosystem/newron-client-collections';
import { FC, useEffect, useState } from 'react';
import { Provider } from 'react-redux';
import { renderLoading } from '@pages/Loading';
import Store from '../../../state/Store';
import { useFetchChartData } from '@utils/collectionUtils/hooks/UseFetchChartData';
import { TradingAssistantButterflyChartInsight } from './Model';
import PgWarningBanner from '@components/collections/warning-banner/pg-warning-banner/PgWarningBanner';
import { getBrandDeepDive, getBrandDependency } from '@services/BrandDependency.service';
import { getAbortController } from '@services/utils/OverviewServiceUtils';
import {
  BrandDeepDiveChartResp,
  BrandDeepDiveChartRespDataLookup,
  BrandDependencyChartSubResp,
} from '@models/BrandDependency';
import { Filters, SubSection } from '@utils/pageConfig/Types';
import { emptyBrandFilter } from '@utils/FilterUtils';
import BrandDependencyCard, {
  BrandDependencyCardProps,
} from '@pages/trading-assistant/brand-dependency-card/BrandDependencyCard';
import { getIsUsingCollectionLiveData, getPeriodLabel } from '@utils/collectionUtils/CollectionUtils';
import { getCountryCodes } from '@utils/CountryUtils';
import { createDataLookup } from '@data-visualization/brand-dependency-chart/BrandDependencyChartUtils';
import MultiStackedChartExportProvider from '@hooks/export/export-stacked-chart/context/MultiStackedChartExportProvider';
import { processExportedImages } from '@hooks/export/ExportChart';
import {
  getSortedDeepDiveBrands,
  useFilteredBrands,
} from '@pages/trading-assistant/supplier-review/SupplierReviewUtils';

enableChartsExporting();

type ButterflyChartInsightRenderer = InsightRendererProps<TradingAssistantButterflyChartInsight>;

const ButterflyChartComponent: FC<ButterflyChartInsightRenderer> = ({ insight, onLoaded, onExportReady }) => {
  const {
    apiParams,
    selectedGrowthType,
    factRevenue,
    brandFilterOptions,
    kpi,
    sortBy,
    appliedCountries,
    appliedProductGroups,
    featureOption,
    retailerClientName,
    timeFrame,
    baseTimePeriod,
    isDeepDiveView,
    features,
    currentViewingBrand,
    effectiveProductType,
  } = insight.data;
  const abortController = getAbortController();
  const [viewingBrand, setViewingBrand] = useState<string>(currentViewingBrand ?? '');
  const [deepDiveChartData, setDeepDiveChartData] = useState<BrandDeepDiveChartResp>();
  const [deepDiveDataLookup, setDeepDiveDataLookup] = useState<Map<string, BrandDeepDiveChartRespDataLookup>>(
    new Map(),
  );

  const { comparisonPeriod, productType, countries, period, clientId, productGroup } = apiParams;
  const { isLoading, chartData } = useFetchChartData({
    apiRequest: async () => {
      const label = await getPeriodLabel({
        clientId,
        periodicity: timeFrame,
        baseTimePeriod,
        comparisonPeriod,
        countryIds: getCountryCodes(countries),
        productType,
        collectionDataType: getIsUsingCollectionLiveData(period, insight.dataType) ? 'live' : insight.dataType,
        productGroups: productGroup,
      });
      const getPeriodFromLabel = RegExp(/\b\w{3,9} \d{4}(?= vs)/i).exec(label)?.[0] ?? period;
      const isUsingCollectionLiveData = getIsUsingCollectionLiveData(getPeriodFromLabel, insight.dataType);
      const params = {
        ...apiParams,
        isUsingCollectionLiveData,
      };
      return isDeepDiveView ? getBrandDeepDive(params, abortController) : getBrandDependency(params, abortController);
    },
    onLoaded,
  });

  useEffect(() => {
    if (isDeepDiveView) setDeepDiveChartData(chartData);
  }, [isDeepDiveView, chartData]);

  const brandFiltersWithDefaultValue: Filters = brandFilterOptions ?? emptyBrandFilter;

  const getFilteredBrands = useFilteredBrands(brandFiltersWithDefaultValue);

  const dataArr: BrandDependencyChartSubResp[] =
    (chartData &&
      ((getFilteredBrands.length ? chartData.selectedBrandsData : chartData[kpi]) as BrandDependencyChartSubResp[])) ??
    [];

  const sortedDeepDiveBrands = getSortedDeepDiveBrands(deepDiveChartData, deepDiveDataLookup, kpi, sortBy);

  useEffect(() => {
    if (isDeepDiveView) {
      setDeepDiveDataLookup(createDataLookup(deepDiveChartData?.dataLookup));
      setViewingBrand(viewingBrand ?? sortedDeepDiveBrands[0] ?? deepDiveChartData?.[kpi]?.[0] ?? '');
    }
  }, [deepDiveChartData]);

  const props: BrandDependencyCardProps = {
    isLoading,
    setIsloading: () => {},
    chartData: dataArr,
    kpi,
    setKpi: () => {},
    sortBy,
    setSortBy: () => {},
    retailerClientName,
    selectedGrowthType,
    factRevenue,
    features,
    bySegment: featureOption,
    featureOption,
    isDeepDiveView,
    viewingBrand,
    setViewingBrand,
    deepDiveBrands: sortedDeepDiveBrands,
    deepDiveDataLookup,
    setFeatureOptions: () => {},
    subSectionForAmplitudeTracking: SubSection.TRADING_ASSISTANT_SUPPLIER_REVIEW,
    appliedCountries,
    period: baseTimePeriod,
    timeFrame,
    comparisonPeriod,
    appliedProductGroups,
    effectiveProductType,
  };

  if (isLoading) return renderLoading();

  return (
    <BrandDependencyCard
      {...props}
      captureOnly={true}
      onCaptureComplete={async (images) => {
        const processed = await processExportedImages(images);
        onExportReady({ exportPng: async () => ({ images: processed }) });
      }}
    />
  );
};

const ButterflyChartInsight: FC<ButterflyChartInsightRenderer> = (props) => {
  return (
    <Provider store={Store}>
      <MultiStackedChartExportProvider>
        <PgWarningBanner
          baseApiParams={{
            clientId: props.insight.data.apiParams.clientId,
            productType: props.insight.data.apiParams.productType,
            periodType: props.insight.data.apiParams.periodType,
            period: props.insight.data.apiParams.period,
            comparisonPeriod: props.insight.data.apiParams.comparisonPeriod,
            countries: props.insight.data.apiParams.countries,
            productGroup: props.insight.data.apiParams.productGroup,
            appliedFilters: props.insight.data.apiParams.appliedFilters,
          }}
          collectionDataType={props.insight.dataType}
        >
          <ButterflyChartComponent {...props} />
        </PgWarningBanner>
      </MultiStackedChartExportProvider>
    </Provider>
  );
};

export default ButterflyChartInsight;
